import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { isInIosApp } from "@/lib/links/in-app";
import { useAgentTrials } from "@/lib/workspace/agent-trials";
import { orpc } from "@/lib/orpc";
import { useCanOrganizationWide } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { BillingAgentTrialsCard } from "./-components/integrations/billing-agent-trials-card";
import { BillingConnect } from "./-components/billing/billing-connect";
import { BillingDetailsCard } from "./-components/billing/billing-details-card";
import { BillingInvoicesCard } from "./-components/billing/billing-invoices-card";
import { BillingOrdersCard } from "./-components/billing/billing-orders-card";
import { BillingOverdueAlert } from "./-components/billing/billing-overdue-alert";
import { BillingPaymentCard } from "./-components/billing/billing-payment-card";
import { BillingPlanCard } from "./-components/billing/billing-plan-card";
import { BillingSubscriptionCard } from "./-components/billing/billing-subscription-card";
import { BillingSummaryCard } from "./-components/billing/billing-summary-card";

const TABS = ["overview", "agents", "payment", "invoices", "details", "plan"] as const;
type Tab = (typeof TABS)[number];
const isTab = (value: unknown): value is Tab => TABS.includes(value as Tab);

export const Route = createFileRoute("/_authenticated/_administration/settings/billing")({
  validateSearch: (search: Record<string, unknown>): { tab?: Tab } =>
    isTab(search.tab) ? { tab: search.tab } : {},
  beforeLoad: ({ context }) => guardRoute(context.me, "billing"),
  component: BillingSettings,
});

/** Settings → Billing: the organization's plan, AI employees, card, invoices and billing details,
 * for whoever manages billing. Read live from Stripe each visit, so what shows here is what Stripe
 * will charge. */
function BillingSettings() {
  /* The organization's billing: a role held at one building never reaches it. */
  const owns = useCanOrganizationWide("organization:manage-billing");
  /* No card form, upgrade or invoice payment inside the iPhone app (App Store 3.1.1). */
  const inApp = isInIosApp();
  const trials = useAgentTrials();
  const navigate = useNavigate({ from: Route.fullPath });
  const { tab = "overview" } = Route.useSearch();
  const enabled = owns && !inApp;
  const overview = useQuery({ ...orpc.administration.billing.overview.queryOptions({}), enabled });
  const account = useQuery({ ...orpc.administration.billing.account.queryOptions({}), enabled });
  const open = (next: string) => {
    if (isTab(next)) void navigate({ search: { tab: next }, replace: true });
  };

  if (!owns || inApp) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Billing" />
        <EmptyState
          title={
            owns ? "Manage billing on the web at app.ppl-ai.com" : "Billing is the organization's"
          }
          description={
            owns
              ? "Your plan, card and invoices are managed there, signed in with this account."
              : "Billing covers every building, so only someone who manages billing for the whole organization can open it."
          }
        />
      </div>
    );
  }

  const header = (
    <PageHeader
      title="Billing"
      meta="Your plan, AI employees, card, invoices and who invoices are made out to."
    />
  );
  /* The summary is a free Core organization's view; a subscription's own page stands without it. */
  const subscribed = Boolean(overview.data?.subscription);
  if (overview.isPending || (account.isPending && !subscribed)) {
    return (
      <div className="flex flex-col gap-6" aria-busy="true">
        {header}
        <Skeleton className="h-10 w-full max-w-xl" />
        <Skeleton className="h-56 w-full" />
      </div>
    );
  }
  if (overview.isError || (account.isError && !subscribed)) {
    const failed = overview.isError ? overview : account;
    return (
      <div className="flex flex-col gap-6">
        {header}
        <ReadFailed
          message="Could not load billing."
          error={failed.error}
          onRetry={() => void Promise.all([overview.refetch(), account.refetch()])}
          retrying={overview.isFetching || account.isFetching}
        />
      </div>
    );
  }

  const subscription = overview.data.subscription;
  const summary = account.data;
  return (
    <div className="flex flex-col gap-6">
      {header}
      {overview.data.configured ? null : (
        <p className="bg-muted text-muted-foreground rounded-lg px-4 py-3 text-sm">
          Payments are not connected on this deployment, so cards and invoices cannot be shown.
        </p>
      )}
      <Tabs value={tab} onValueChange={open}>
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {trials.locks ? <TabsTrigger value="agents">AI employees</TabsTrigger> : null}
          <TabsTrigger value="payment">Payment method</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="details">Billing details</TabsTrigger>
          <TabsTrigger value="plan">Plan</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4 flex flex-col gap-4">
          {summary?.overdue ? <BillingOverdueAlert overdue={summary.overdue} open={open} /> : null}
          {subscription ? (
            <BillingSubscriptionCard subscription={subscription} />
          ) : summary ? (
            <BillingSummaryCard account={summary} open={open} />
          ) : null}
          {!subscription && !trials.locks && overview.data.configured ? (
            <BillingConnect email={overview.data.connectable?.email ?? null} />
          ) : null}
        </TabsContent>
        {trials.locks ? (
          <TabsContent value="agents" className="mt-4">
            <BillingAgentTrialsCard />
          </TabsContent>
        ) : null}
        <TabsContent value="payment" className="mt-4">
          <BillingPaymentCard card={subscription?.card ?? summary?.card ?? null} />
        </TabsContent>
        <TabsContent value="invoices" className="mt-4 flex flex-col gap-4">
          <BillingInvoicesCard
            invoices={subscription ? overview.data.invoices : (summary?.invoices ?? [])}
          />
          {subscription ? <BillingOrdersCard /> : null}
        </TabsContent>
        <TabsContent value="details" className="mt-4">
          <BillingDetailsCard />
        </TabsContent>
        <TabsContent value="plan" className="mt-4">
          {summary ? (
            <BillingPlanCard plan={summary.plan} />
          ) : (
            <ReadFailed
              message="Could not load your plan."
              error={account.error}
              onRetry={() => void account.refetch()}
              retrying={account.isFetching}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
